import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Shared markdown renderer for admin-authored and agent-authored text (ops
// decision bodies, run summaries, poll questions), stored as markdown.
// Styled by hand (no @tailwindcss/typography plugin in this project) to
// match the admin UI's own tokens; deliberately no raw HTML support, since
// this renders untrusted text.

const components: Components = {
  p: ({ children }) => <p className="my-1.5 leading-relaxed">{children}</p>,
  h1: ({ children }) => <h1 className="text-[1.3em] leading-tight font-bold mt-3 mb-1.5 first:mt-0">{children}</h1>,
  h2: ({ children }) => <h2 className="text-[1.15em] leading-tight font-bold mt-3 mb-1.5 first:mt-0">{children}</h2>,
  h3: ({ children }) => <h3 className="text-[1em] font-bold mt-2 mb-1 first:mt-0">{children}</h3>,
  ul: ({ children }) => <ul className="my-1.5 pl-4 list-disc space-y-0.5">{children}</ul>,
  ol: ({ children }) => <ol className="my-1.5 pl-4 list-decimal space-y-0.5">{children}</ol>,
  li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  a: ({ children, href }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary-500 hover:underline"
    >
      {children}
    </a>
  ),
  code: ({ children, className }) => {
    // Block code (has a language className from a fenced block) vs inline.
    const isBlock = /language-/.test(className ?? '');
    if (isBlock) return <code className={className}>{children}</code>;
    return (
      <code className="bg-neutral-100 dark:bg-neutral-800 px-1 py-0.5 rounded text-[0.85em] font-mono">
        {children}
      </code>
    );
  },
  pre: ({ children }) => (
    <pre className="my-2 p-2 rounded-lg bg-neutral-100 dark:bg-neutral-800 overflow-x-auto text-xs font-mono">
      {children}
    </pre>
  ),
  blockquote: ({ children }) => (
    <blockquote className="my-1.5 pl-3 border-l-2 border-neutral-300 dark:border-neutral-600 text-dark-muted">
      {children}
    </blockquote>
  ),
  hr: () => <hr className="my-2 border-neutral-200 dark:border-neutral-700" />,
  // Images only from our own storage bucket (uploads from the app); anything
  // else becomes a plain link so untrusted text can't embed remote images.
  img: ({ src, alt }) => {
    const url = typeof src === 'string' ? src : '';
    if (!/^https:\/\/firebasestorage\.googleapis\.com\//.test(url)) {
      return url ? (
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:underline">
          {alt || url}
        </a>
      ) : null;
    }
    return (
      <img
        src={url}
        alt={alt ?? ''}
        loading="lazy"
        className="inline-block align-top max-h-72 max-w-full rounded-lg mr-2 mb-2 bg-neutral-100 dark:bg-neutral-800"
      />
    );
  },
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto">
      <table className="w-full text-xs border-collapse">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="border-b border-neutral-200 dark:border-neutral-700">{children}</thead>
  ),
  th: ({ children }) => <th className="text-left font-semibold py-1 pr-3">{children}</th>,
  td: ({ children }) => (
    <td className="py-1 pr-3 border-t border-neutral-100 dark:border-neutral-800">{children}</td>
  ),
};

const sizeClasses = { sm: 'text-sm', base: 'text-base', lg: 'text-lg' } as const;

export default function Markdown({
  children,
  className = '',
  size = 'sm',
  components: overrides,
}: {
  children: string;
  className?: string;
  size?: keyof typeof sizeClasses;
  components?: Components;
}) {
  return (
    <div className={`${sizeClasses[size]} ${className}`}>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={overrides ? { ...components, ...overrides } : components}>
        {children}
      </ReactMarkdown>
    </div>
  );
}
